import type { PanelIssue } from '@/types';

/**
 * Response windows by severity.
 *
 * The design colours the SLA column but does not say how long each window is,
 * so these are ours: a critical portal fault is answered within a working
 * morning, a low one within the week. Change them here and the column follows.
 */
export const SLA_HOURS: Record<PanelIssue['severity'], number> = {
  critical: 4,
  high: 24,
  medium: 72,
  low: 168,
};

export type SlaTone = 'caution' | 'negative' | 'positive' | 'inert';

export interface SlaState {
  label: string;
  tone: SlaTone;
  /** When the window closes, for the hover title. */
  due: Date;
}

const HOUR = 3_600_000;

function span(ms: number): string {
  const hours = Math.max(1, Math.round(Math.abs(ms) / HOUR));
  return hours < 48 ? `${String(hours)}h` : `${String(Math.round(hours / 24))}d`;
}

/**
 * The design's rule: amber while inside the window, red once breached - the
 * same amber and red used for licence expiry and crash severity. A resolved
 * issue says whether it was met, quietly.
 */
export function slaFor(issue: PanelIssue, now: number = Date.now()): SlaState {
  const due = new Date(new Date(issue.createdAt).getTime() + SLA_HOURS[issue.severity] * HOUR);
  if (issue.status === 'resolved') {
    const resolved = issue.resolvedAt ? new Date(issue.resolvedAt).getTime() : now;
    return resolved <= due.getTime()
      ? { label: 'Met', tone: 'positive', due }
      : { label: 'Missed', tone: 'inert', due };
  }
  const left = due.getTime() - now;
  return left > 0
    ? { label: `${span(left)} left`, tone: 'caution', due }
    : { label: `Breached ${span(left)}`, tone: 'negative', due };
}
